package org.teddapp.project.sync

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import io.ktor.client.HttpClient
import io.ktor.client.plugins.HttpTimeout
import io.ktor.client.plugins.websocket.WebSockets
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import org.teddapp.project.core.CoreSession
import org.teddapp.project.db.TeddRepositories
import org.teddapp.project.db.nowMillis
import org.teddapp.project.server.PreferencesServerStore
import org.teddapp.project.server.ServerSession
import org.teddapp.project.ui.AppSettings
import org.teddapp.project.ui.settings.SupabaseConnector
import org.teddapp.project.ui.settings.SyncBindings

/**
 * Ce que l'entrée d'une plateforme fournit aux écrans pour la synchronisation :
 * la session serveur, le branchement actif, et le connecteur Supabase tant qu'il sert.
 */
class TeddSync(
    val session: ServerSession,
    /** Le distant actif : le serveur teddApp si on y est connecté, sinon Supabase s'il est configuré. */
    val bindings: SyncBindings?,
    /** Null une fois connecté au serveur : il n'y a plus de Supabase à configurer. */
    val supabaseConnector: SupabaseConnector?,
)

/**
 * Tient la synchronisation pour toute l'application, de la même façon sur chaque plateforme.
 *
 * Un seul distant à la fois. Connecté au serveur teddApp, c'est lui : le branchement
 * Supabase est arrêté et ses identifiants oubliés. Sinon, Supabase tant qu'il est
 * configuré. Chacun vit dans sa propre portée, pour l'arrêter net quand on change.
 *
 * @param env lecture d'une variable d'environnement, pour l'amorçage de Supabase.
 */
@Composable
fun rememberTeddSync(
    repos: TeddRepositories,
    appSettings: AppSettings,
    syncScope: CoroutineScope,
    deviceName: String,
    platform: String,
    env: (String) -> String? = { null },
    coreSession: CoreSession? = null,
    /**
     * Adresse du serveur, imposée : la production, sauf si la plateforme en donne une autre
     * (le web déduit la sienne du domaine) ou si `TEDD_SERVER_URL` est posée (développement).
     */
    serverUrl: String = "",
): TeddSync {
    val fixedUrl = serverUrl.ifBlank { env("TEDD_SERVER_URL")?.takeIf { it.isNotBlank() } ?: ServerSession.PRODUCTION_URL }
    fun childScope() = CoroutineScope(syncScope.coroutineContext + SupervisorJob(syncScope.coroutineContext[Job]))

    val store = remember { PreferencesServerStore(repos.preferences) }
    // Relue avant le premier affichage : on sait tout de suite quel distant brancher.
    val http = remember { HttpClient { install(HttpTimeout) { requestTimeoutMillis = 30_000 }; install(WebSockets) } }
    val session = remember {
        ServerSession(
            http = http,
            store = store,
            deviceName = deviceName,
            platform = platform,
            now = { nowMillis() },
            defaultUrl = fixedUrl,
        ).also { restoreAtStartup(it) }
    }

    val supabaseScope = remember { childScope() }
    var supabaseBindings by remember {
        val url = appSettings.supabaseUrl.ifBlank { env("SUPABASE_URL").orEmpty() }
        val key = appSettings.supabaseAnonKey.ifBlank { env("SUPABASE_ANON_KEY").orEmpty() }
        mutableStateOf(
            if (!session.connected && url.isNotBlank() && key.isNotBlank()) {
                createSyncBindings(repos.db, appSettings, supabaseScope, url, key, coreSession)
            } else null,
        )
    }
    val supabaseConnector = remember {
        CommonSupabaseConnector(
            db = repos.db,
            appSettings = appSettings,
            scope = supabaseScope,
            setBindings = { supabaseBindings = it },
            coreSession = coreSession,
        )
    }

    var serverBindings by remember { mutableStateOf<SyncBindings?>(null) }
    DisposableEffect(session.connected, session.userId) {
        val scope = childScope()
        if (session.connected) {
            if (supabaseBindings != null || appSettings.supabaseUrl.isNotBlank()) supabaseConnector.disconnect()
            supabaseScope.coroutineContext[Job]?.cancel()
            appSettings.applySyncEnabled(true)
            serverBindings = createServerSyncBindings(repos.db, appSettings, scope, session, store, http, coreSession)
        }
        onDispose {
            scope.coroutineContext[Job]?.cancel()
            // Déconnecté, le journal ne part plus : il reprendra au prochain branchement.
            if (serverBindings != null) { coreSession?.transport = null; coreSession?.remote = null }
            serverBindings = null
        }
    }

    return TeddSync(
        session = session,
        bindings = serverBindings ?: supabaseBindings,
        supabaseConnector = if (session.connected) null else supabaseConnector,
    )
}
